/**
 * Template Build API Enhancement
 *
 * Enhances the generated templates API with cache tags and custom file upload mutation.
 */

import { baseApi } from '@store/baseApi';
import type {
  TemplateBuildUploadResponse,
} from '@store/generated/templates';
import { enhancedApi } from '@store/generated/templates';

// ============================================================================
// Cache Tag Types
// ============================================================================

const TEMPLATE_BUILD_TAG = 'TemplateBuild' as const;
const TEMPLATE_LIST_TAG = 'TemplateList' as const;

// ============================================================================
// Enhanced API with Cache Tags
// ============================================================================

export const templateBuildApi = enhancedApi.enhanceEndpoints({
  addTagTypes: [TEMPLATE_BUILD_TAG, TEMPLATE_LIST_TAG],
  endpoints: {
    // Status polling endpoint
    getV2ProviderNotetemplatesBuildByTaskIdStatus: {
      providesTags: (result, error, { taskId }) => [
        { type: TEMPLATE_BUILD_TAG, id: taskId },
      ],
    },
    // Confirm endpoint invalidates both build and list
    postV2ProviderNotetemplatesBuildByTaskIdConfirm: {
      invalidatesTags: (result, error, { taskId }) => [
        { type: TEMPLATE_BUILD_TAG, id: taskId },
        { type: TEMPLATE_LIST_TAG, id: 'LIST' },
      ],
    },
    // Save endpoint invalidates both build and list (refreshes template list)
    postV2ProviderNotetemplatesBuildByTaskIdSave: {
      invalidatesTags: (result, error, { taskId }) => [
        { type: TEMPLATE_BUILD_TAG, id: taskId },
        { type: TEMPLATE_LIST_TAG, id: 'LIST' },
      ],
    },
    // Get all templates
    getV2ProviderNotetemplates: {
      providesTags: [{ type: TEMPLATE_LIST_TAG, id: 'LIST' }],
    },
  },
});

// ============================================================================
// Custom File Upload API
// ============================================================================

/**
 * Custom API for file uploads that require FormData.
 * The generated API doesn't support FormData properly, so we need a custom endpoint.
 */
export const templateBuildUploadApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    /**
     * Upload a PDF file to start template build
     */
    uploadPdfForTemplateBuild: builder.mutation<
      TemplateBuildUploadResponse,
      { file: File }
    >({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append('file', file);

        return {
          url: '/v2/provider/notetemplates/build',
          method: 'POST',
          body: formData,
          // Don't set Content-Type - browser will set it with boundary
          formData: true,
        };
      },
    }),

    /**
     * Upload text content to start template build
     */
    uploadTextForTemplateBuild: builder.mutation<
      TemplateBuildUploadResponse,
      { text: string; filename?: string }
    >({
      query: ({ text, filename = 'pasted-content.txt' }) => ({
        url: '/v2/provider/notetemplates/build',
        method: 'POST',
        body: {
          text_content: text,
          filename,
        },
      }),
    }),
  }),
  overrideExisting: false,
});

// ============================================================================
// User-Friendly Hook Exports
// ============================================================================

// Upload hooks
export const {
  useUploadPdfForTemplateBuildMutation,
  useUploadTextForTemplateBuildMutation,
} = templateBuildUploadApi;

// Status polling hook (uses refetch interval)
export const useGetTemplateBuildStatusQuery =
  templateBuildApi.useGetV2ProviderNotetemplatesBuildByTaskIdStatusQuery;

// Confirm hook
export const useConfirmTemplateBuildMutation =
  templateBuildApi.usePostV2ProviderNotetemplatesBuildByTaskIdConfirmMutation;

// Save template hook
export const useSaveTemplateBuildMutation =
  templateBuildApi.usePostV2ProviderNotetemplatesBuildByTaskIdSaveMutation;

// Build from existing template hook
export const useBuildFromTemplateMutation =
  templateBuildApi.usePostV2ProviderNotetemplatesBuildFromTemplateMutation;

// Template list hook
export const useGetTemplatesQuery = templateBuildApi.useGetV2ProviderNotetemplatesQuery;

// ============================================================================
// API Utilities
// ============================================================================

/**
 * Convert API status to frontend BuildStatus type
 */
export function toBuildStatus(
  apiStatus: string,
): 'idle' | 'uploading' | 'processing' | 'structure_ready' | 'ready_for_review' | 'completed' | 'failed' {
  switch (apiStatus) {
    case 'processing':
    case 'analyzing':
    case 'pending':
      return 'processing';
    case 'structure_ready':
    case 'generating_examples':
      return 'structure_ready';
    case 'ready_for_review':
    case 'ready':
      return 'ready_for_review';
    case 'completed':
    case 'saved':
      return 'completed';
    case 'failed':
    case 'error':
      return 'failed';
    default:
      return 'idle';
  }
}

/**
 * Check if status indicates processing is still ongoing
 */
export function isProcessing(status: string): boolean {
  return status === 'processing' || status === 'analyzing' || status === 'pending' || status === 'structure_ready' || status === 'generating_examples';
}

/**
 * Check if status indicates build is ready for chat interaction
 * Chat is available when structure is ready (even if examples are still generating)
 */
export function isReadyForReview(status: string): boolean {
  return status === 'ready_for_review' || status === 'ready' || status === 'structure_ready' || status === 'generating_examples';
}

